import React, { useEffect, useRef, useState } from "react";
import { theme } from "../../data/theme";

/**
 * PlanMenu — one ⋯ beside the plan picker, holding every plan OPERATION.
 *
 * 🔴 WHAT THIS REPLACED, AND WHY IT IS BETTER RATHER THAN TIDIER. The toolbar
 * was a dropdown plus ten buttons on one row, five of them destructive and four
 * of them red. Every one is rare — you name a plan once, you sweep generated
 * plans once — while the two you actually press mid-planning sat at the far end
 * of that row, behind the wreckage. Collapsing the rare seven behind one
 * affordance is what lets the two optimize buttons and the view toggle read as
 * the toolbar's actual content. Nothing was removed to get there.
 *
 * 🔴 THE TWO-STEP CONFIRMS COME WITH THEM, unchanged in kind: click once to
 * arm, again within three seconds to fire. A native confirm() blocks the page
 * and was already rejected in this feature once. Being inside a menu does NOT
 * make a delete safe enough to go one-click — the menu hides the button, it
 * does not hide the consequence.
 *
 * ONE `armed` KEY, NOT THREE BOOLEANS. Three independent flags let two items
 * sit armed and red at the same time, which reads as "both of these are about
 * to happen". Arming one disarms the others by construction.
 *
 * The click-away/Escape handling is BoardMenu's, deliberately — one menu
 * behaviour in the app, and that one is already proven in a live draft.
 */
export default function PlanMenu({
  planName = "plan",
  planCount = 1,
  generatedCount = 0,
  generatedNames = [],
  onNew,
  onDuplicate,
  onRename,
  onDelete,
  onDeleteGenerated,
  onKeepOnly,
  onExport,
}) {
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState(null);   // 'delete' | 'sweep' | 'keepOnly'
  const wrapRef = useRef(null);
  const armTimer = useRef(null);

  const disarm = () => { clearTimeout(armTimer.current); setArmed(null); };
  const arm = (key) => {
    clearTimeout(armTimer.current);
    setArmed(key);
    armTimer.current = setTimeout(() => setArmed(null), 3000);
  };

  // Closing must also disarm. A menu reopened ten minutes later with a delete
  // still primed is a click away from taking a plan the user forgot about.
  useEffect(() => { if (!open) disarm(); }, [open]);
  useEffect(() => () => clearTimeout(armTimer.current), []);

  // Bound only while open — see BoardMenu.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const item = {
    display: "block", width: "100%", textAlign: "left",
    background: "transparent", border: "none", borderRadius: theme.radius.sm,
    color: theme.colors.text.primary, fontFamily: "inherit",
    fontSize: theme.fontSize.sub, padding: "7px 10px", cursor: "pointer",
  };
  const rule = { height: 1, background: theme.colors.border.subtle, margin: "8px 0" };

  /** A plain item: does its thing and closes. */
  const Item = ({ id, label, onClick, title, disabled }) => (
    <button
      type="button"
      role="menuitem"
      data-testid={`plan-menu-${id}`}
      title={title}
      disabled={disabled}
      onClick={() => { setOpen(false); if (onClick) onClick(); }}
      style={{
        ...item,
        color: disabled ? theme.colors.text.faint : theme.colors.text.primary,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >{label}</button>
  );

  /**
   * A destructive item. First click arms and KEEPS THE MENU OPEN — you have to
   * be able to see what you armed — second click fires and closes.
   *
   * 🔴 DISABLED RATHER THAN HIDDEN when it cannot apply, with the reason in the
   * title. An item that vanishes reads as a bug; one that explains itself does
   * not. (Delete-all-optimized is never disabled: at zero it reports what it
   * found, because disabling it would hide the fact that legacy plans carry no
   * marker. Same silent-no-op failure the fully-committed optimize path had.)
   */
  const DangerItem = ({ id, label, armedLabel, onConfirm, title, disabled }) => {
    const isArmed = armed === id;
    return (
      <button
        type="button"
        role="menuitem"
        data-testid={`plan-menu-${id}`}
        title={title}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          if (isArmed) { disarm(); setOpen(false); if (onConfirm) onConfirm(); }
          else arm(id);
        }}
        style={{
          ...item,
          background: isArmed ? "rgba(239,68,68,0.12)" : "transparent",
          color: disabled ? theme.colors.text.faint : theme.colors.status.danger,
          fontWeight: isArmed ? theme.fontWeight.bold : theme.fontWeight.regular,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >{isArmed ? armedLabel : label}</button>
    );
  };

  const others = planCount - 1;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        data-testid="plan-menu"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Plan actions"
        title="New, duplicate, rename, delete, export"
        style={{
          padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
          borderRadius: theme.radius.md,
          border: `1px solid ${open ? theme.colors.border.brand : theme.colors.border.default}`,
          background: open ? theme.helpers.brandBg(0.12) : theme.colors.bg.raised,
          color: open ? theme.colors.brand.orange : theme.colors.text.secondary,
          fontSize: theme.fontSize.sub,
          fontWeight: theme.fontWeight.bold,
          fontFamily: theme.fonts.body,
          cursor: "pointer",
        }}
      >⋯</button>

      {open && (
        <div
          data-testid="plan-menu-panel"
          role="menu"
          style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 130,
            width: 236, maxHeight: "70vh", overflowY: "auto",
            background: theme.colors.extended.modalBg,
            border: `1px solid ${theme.colors.border.default}`,
            borderRadius: theme.radius.lg, padding: 8,
            boxShadow: "0 18px 44px rgba(0,0,0,0.55)",
          }}
        >
          <Item id="new" label="New plan" onClick={onNew} />
          <Item id="duplicate" label="Duplicate" onClick={onDuplicate} />
          {/* 🔴 KEPT, THOUGH THE NAME IS NOW EDITABLE IN PLACE (PlanNameField).
              This item is how somebody who already knows this menu finds the
              feature, and it is the keyboard-reachable path; the inline field is
              how everybody else discovers renaming exists at all — which was the
              actual problem, since the capability had been here all along.

              Both call the same renamePlan, so both CLAIM the plan: a renamed
              plan leaves the generated set and the sweep below can no longer
              take it. The prompt() lives at the call site — free text is a
              different kind of question from these yes/no confirms. */}
          <Item id="rename" label="Rename…" onClick={onRename} />

          <div style={rule} />

          <DangerItem
            id="delete"
            label="Delete this plan"
            armedLabel={`Delete ${planName}?`}
            disabled={planCount <= 1}
            title={planCount <= 1
              ? "There is only one plan — deleting it would leave nothing to work in."
              : "Delete this plan"}
            onConfirm={onDelete}
          />
          {/* Filters on the generated flag, so it can never take a hand-built
              plan however it is named — nor one you have RENAMED, which is what
              naming a plan now means (see isGenerated). */}
          <DangerItem
            id="sweep"
            label="Delete all optimized"
            armedLabel={generatedCount > 0 ? `Delete ${generatedCount} optimized?` : "Delete all optimized?"}
            title="Remove plans the optimizer generated. Hand-built and renamed plans are never touched."
            onConfirm={onDeleteGenerated}
          />
          {/* 🔴 THE ARMED STATE NAMES WHAT IT WILL TAKE. A count cannot be
              checked against anything: "Delete 14 optimized?" is the same
              sentence whether or not the fourteen include the plan you are
              drafting from tonight. With twenty test plans in play the list is
              the only thing that makes the confirm a decision rather than a
              formality. Scrollable rather than capped — a truncated list would
              hide exactly the row somebody is looking for. */}
          {armed === "sweep" && generatedNames.length > 0 && (
            <div
              data-testid="plan-menu-sweep-list"
              style={{
                maxHeight: 132, overflowY: "auto",
                margin: "2px 10px 6px",
                fontSize: theme.fontSize.caption,
                lineHeight: 1.5,
                color: theme.colors.text.faint,
              }}
            >
              {generatedNames.map((n, i) => (
                <div key={`${n}-${i}`} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{n}</div>
              ))}
            </div>
          )}
          {/* 🔴 The flagless escape hatch. No provenance test at all — you are
              looking at the plan you want and saying so — which is why it
              handles legacy piles the flag-based sweep is blind to. */}
          <DangerItem
            id="keepOnly"
            label="Keep only this plan"
            armedLabel={`Delete ${others} other plan${others === 1 ? "" : "s"}?`}
            disabled={planCount <= 1}
            title={planCount <= 1
              ? "There is only one plan — there is nothing else to remove."
              : "Delete every plan except the one you are looking at, whatever created it."}
            onConfirm={onKeepOnly}
          />

          <div style={rule} />

          <Item id="export" label="Export" title="Download every plan as a JSON file" onClick={onExport} />
        </div>
      )}
    </div>
  );
}
